import { useEffect, useState } from 'react'
import {
  Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../auth/AuthProvider'
import {
  PERMESSI_VALIDI, SchemaOperatore, aggiornaPermessi, creaOperatore, elencaAccount, eliminaAccount,
  type Permesso,
} from './account.api'

export const ETICHETTA_PERMESSO: Record<Permesso, string> = {
  dichiarazioni_ad1: 'Dichiarazioni AD-1',
  scadenziari: 'Scadenziari',
  registri_letture: 'Registri letture',
  praticheenergia_green: 'PraticheEnergia Green',
  vede_tutti_impianti: 'Vede tutti gli impianti',
  gestione: 'Gestione',
}

interface Operatore {
  id: number
  email: string
  nome: string
  account_permesso: { permesso: string }[]
}

const NESSUN_OPERATORE: Operatore[] = []
const ordinati = (p: readonly string[]) => PERMESSI_VALIDI.filter((x) => p.includes(x))

export function AccountEPermessi() {
  const queryClient = useQueryClient()
  const { sessione } = useAuth()
  const mia = sessione?.user?.email ?? ''
  const [scelti, setScelti] = useState<Record<number, Permesso[]>>({})
  const [errore, setErrore] = useState<string | null>(null)
  const [messaggio, setMessaggio] = useState<string | null>(null)
  const [nuovo, setNuovo] = useState<{ nome: string; email: string; permessi: Permesso[] } | null>(null)

  const { data: operatori = NESSUN_OPERATORE } = useQuery({
    queryKey: ['operatori'],
    queryFn: () => elencaAccount() as Promise<Operatore[]>,
  })

  useEffect(() => {
    setScelti(Object.fromEntries(operatori.map((o) => [o.id, ordinati(o.account_permesso.map((p) => p.permesso))])))
  }, [operatori])

  const commuta = (id: number, p: Permesso) =>
    setScelti((s) => {
      const attuali = s[id] ?? []
      return { ...s, [id]: ordinati(attuali.includes(p) ? attuali.filter((x) => x !== p) : [...attuali, p]) }
    })

  async function conEsito(azione: () => Promise<void>, esito: string) {
    setErrore(null)
    setMessaggio(null)
    try {
      await azione()
      await queryClient.invalidateQueries({ queryKey: ['operatori'] })
      setMessaggio(esito)
    } catch (e) {
      setErrore(e instanceof Error ? e.message : 'errore imprevisto')
    }
  }

  async function suCrea() {
    if (nuovo === null) return
    const esito = SchemaOperatore.safeParse(nuovo)
    if (!esito.success) {
      setErrore(esito.error.issues[0]!.message)
      return
    }
    setErrore(null)
    try {
      await creaOperatore(esito.data)
      setNuovo(null)
      await queryClient.invalidateQueries({ queryKey: ['operatori'] })
      // crea-operatore assegna una password casuale: il primo accesso passa da
      // «password dimenticata», senza che nessuno comunichi una password.
      setMessaggio(`${esito.data.nome} puo entrare dalla pagina di accesso con «password dimenticata».`)
    } catch (e) {
      setErrore(e instanceof Error ? e.message : 'errore imprevisto')
    }
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Account e permessi
      </Typography>
      {/* Con il dialogo aperto il resto della pagina e' nascosto: l'errore va li' dentro. */}
      {errore !== null && nuovo === null && <Alert severity="error" role="alert" sx={{ mb: 2 }}>{errore}</Alert>}
      {messaggio !== null && <Alert severity="info" sx={{ mb: 2 }}>{messaggio}</Alert>}

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Operatore</TableCell>
            <TableCell>Permessi</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {operatori.map((o) => {
            const sonoIo = o.email === mia
            return (
              <TableRow key={o.id}>
                <TableCell>
                  <Typography variant="body2">{o.nome}</Typography>
                  <Typography variant="caption" color="text.secondary">{o.email}</Typography>
                </TableCell>
                <TableCell>
                  <Stack direction="row" flexWrap="wrap">
                    {PERMESSI_VALIDI.map((p) => (
                      <FormControlLabel
                        key={p}
                        control={
                          <Checkbox
                            size="small"
                            checked={(scelti[o.id] ?? []).includes(p)}
                            // Togliersi `gestione` da soli lascerebbe il partner
                            // senza nessuno che possa rimetterlo.
                            disabled={sonoIo && p === 'gestione'}
                            onChange={() => commuta(o.id, p)}
                            inputProps={{ 'aria-label': `${o.nome}: ${ETICHETTA_PERMESSO[p]}` }}
                          />
                        }
                        label={ETICHETTA_PERMESSO[p]}
                      />
                    ))}
                  </Stack>
                </TableCell>
                <TableCell align="right">
                  <Button
                    size="small"
                    onClick={() =>
                      void conEsito(
                        () => aggiornaPermessi(o.id, scelti[o.id] ?? []),
                        `Permessi di ${o.nome} salvati`,
                      )
                    }
                  >
                    Salva permessi di {o.nome}
                  </Button>
                  {!sonoIo && (
                    <Button
                      size="small"
                      color="error"
                      onClick={() => {
                        if (!window.confirm(`Eliminare l'operatore ${o.nome}?`)) return
                        void conEsito(() => eliminaAccount(o.id), `${o.nome} eliminato`)
                      }}
                    >
                      Elimina {o.nome}
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>

      <Button variant="outlined" sx={{ mt: 2 }} onClick={() => setNuovo({ nome: '', email: '', permessi: [] })}>
        Aggiungi operatore
      </Button>

      <Dialog open={nuovo !== null} onClose={() => setNuovo(null)} fullWidth maxWidth="sm">
        <DialogTitle>Nuovo operatore</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {errore !== null && <Alert severity="error" role="alert">{errore}</Alert>}
            <TextField
              size="small"
              label="Nome"
              value={nuovo?.nome ?? ''}
              onChange={(e) => setNuovo((n) => (n === null ? n : { ...n, nome: e.target.value }))}
            />
            <TextField
              size="small"
              label="Email"
              value={nuovo?.email ?? ''}
              onChange={(e) => setNuovo((n) => (n === null ? n : { ...n, email: e.target.value }))}
            />
            <Box>
              {PERMESSI_VALIDI.map((p) => (
                <FormControlLabel
                  key={p}
                  control={
                    <Checkbox
                      size="small"
                      checked={(nuovo?.permessi ?? []).includes(p)}
                      onChange={() =>
                        setNuovo((n) =>
                          n === null
                            ? n
                            : {
                                ...n,
                                permessi: ordinati(
                                  n.permessi.includes(p) ? n.permessi.filter((x) => x !== p) : [...n.permessi, p],
                                ),
                              },
                        )
                      }
                      inputProps={{ 'aria-label': ETICHETTA_PERMESSO[p] }}
                    />
                  }
                  label={ETICHETTA_PERMESSO[p]}
                />
              ))}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNuovo(null)}>Annulla</Button>
          <Button variant="contained" onClick={() => void suCrea()}>
            Crea operatore
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
